<script setup>
// Calendário da agenda (ver AGENDA.md): dia (uma coluna por profissional) ou semana
// (uma coluna por dia). Clique num horário para marcar; numa consulta para ver/editar.
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AgendaPageLayout from '../components/AgendaPageLayout.vue';
import AgendaCalendar from '../components/AgendaCalendar.vue';
import AppointmentDialog from '../components/AppointmentDialog.vue';
import agendaAPI from 'dashboard/api/agenda';
import { useAgendaData } from '../composables/useAgendaData';
import {
  addDays,
  datePart,
  formatDayLabel,
  formatShortDay,
  formatWeekLabel,
  minutesOfDay,
  minutesToTime,
  nowMinuteIn,
  pastUntilMinute,
  startOfWeek,
  todayIn,
  weekdayOf,
} from '../helpers/agendaTime';

const REFRESH_INTERVAL = 60000;
const VIEW_STORAGE_KEY = 'agenda.view';
const DEFAULT_START = 8 * 60;
const DEFAULT_END = 18 * 60;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { isAdmin } = useAdmin();
const {
  setting,
  professionals,
  hasLoaded,
  professionalById,
  periodsFor,
  fetchAll,
} = useAgendaData();

const DATE_FORMAT = /^\d{4}-\d{2}-\d{2}$/;

// Visão e data vêm do endereço (?view=week&date=2026-10-19); sem isso, a última visão usada e hoje.
const readView = () => {
  if (['day', 'week'].includes(route.query.view)) return route.query.view;
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'week' ? 'week' : 'day';
  } catch {
    return 'day';
  }
};
const queryDate = () =>
  DATE_FORMAT.test(route.query.date || '') ? route.query.date : null;

const view = ref(readView());
const date = ref(queryDate() || todayIn(setting.value.timezone));
const professionalFilter = ref('');
const appointments = ref([]);
const blocks = ref([]);
const isLoadingRange = ref(false);
const now = ref(new Date());
const appointmentDialog = ref(null);
let refreshTimer = null;

const today = computed(() => todayIn(setting.value.timezone, now.value));
const nowMinute = computed(() =>
  nowMinuteIn(setting.value.timezone, now.value)
);
const pastUntil = day => pastUntilMinute(day, today.value, nowMinute.value);

const rangeStart = computed(() =>
  view.value === 'week' ? startOfWeek(date.value) : date.value
);
const rangeDays = computed(() => (view.value === 'week' ? 7 : 1));
const rangeEnd = computed(() => addDays(rangeStart.value, rangeDays.value));
const rangeLabel = computed(() =>
  view.value === 'week'
    ? formatWeekLabel(rangeStart.value)
    : formatDayLabel(date.value)
);

const filterOptions = computed(() => [
  { value: '', label: t('AGENDA.PAGE.ALL_PROFESSIONALS') },
  ...professionals.value
    .filter(item => item.active)
    .map(item => ({ value: item.id, label: `${item.name} · ${item.specialty}` })),
]);

// Ativos, mais inativos que ainda têm consulta no período (para não "sumir" consulta).
const visibleProfessionals = computed(() =>
  professionals.value.filter(
    item =>
      (!professionalFilter.value || item.id === Number(professionalFilter.value)) &&
      (item.active ||
        appointments.value.some(appointment => appointment.professional_id === item.id))
  )
);

const appointmentMinutes = (appointment, day) => ({
  ...appointment,
  color: professionalById(appointment.professional_id)?.color || '#64748B',
  start: datePart(appointment.starts_at) < day ? 0 : minutesOfDay(appointment.starts_at),
  end: datePart(appointment.ends_at) > day ? 24 * 60 : minutesOfDay(appointment.ends_at),
});

const appointmentsOn = (day, professionalIds) =>
  appointments.value
    .filter(
      item =>
        professionalIds.includes(item.professional_id) &&
        datePart(item.starts_at) <= day &&
        datePart(item.ends_at) >= day
    )
    .map(item => appointmentMinutes(item, day))
    .filter(item => item.end > item.start);

const blocksOn = (day, professionalId) =>
  blocks.value
    .filter(
      item =>
        item.professional_id === professionalId &&
        datePart(item.starts_at) <= day &&
        datePart(item.ends_at) >= day
    )
    .map(item => ({
      id: item.id,
      reason: item.reason,
      start: datePart(item.starts_at) < day ? 0 : minutesOfDay(item.starts_at),
      end: datePart(item.ends_at) > day ? 24 * 60 : minutesOfDay(item.ends_at),
    }))
    .filter(item => item.end > item.start);

const countLabel = list => t('AGENDA.PAGE.APPOINTMENTS_COUNT', list.length);

const dayColumns = computed(() =>
  visibleProfessionals.value.map(item => {
    const periods = periodsFor(item, weekdayOf(date.value));
    const list = appointmentsOn(date.value, [item.id]);
    return {
      key: `p${item.id}`,
      isProfessional: true,
      professionalId: item.id,
      title: item.name,
      subtitle: `${item.specialty} · ${countLabel(list)}`,
      color: item.color,
      date: date.value,
      pastUntil: pastUntil(date.value),
      periods,
      snapPeriods: periods,
      duration: item.duration_minutes,
      blocks: blocksOn(date.value, item.id),
      appointments: list,
    };
  })
);

const weekColumns = computed(() => {
  const selected = visibleProfessionals.value;
  const single = professionalFilter.value && selected.length === 1 ? selected[0] : null;
  const ids = selected.map(item => item.id);
  return Array.from({ length: 7 }, (_, index) => {
    const day = addDays(rangeStart.value, index);
    const weekday = weekdayOf(day);
    const periods = selected.flatMap(item => periodsFor(item, weekday));
    const list = appointmentsOn(day, ids);
    return {
      key: day,
      isProfessional: false,
      professionalId: single?.id,
      title: formatShortDay(day),
      subtitle: countLabel(list),
      date: day,
      pastUntil: pastUntil(day),
      periods,
      snapPeriods: single ? periods : [],
      duration: single ? single.duration_minutes : 15,
      blocks: single ? blocksOn(day, single.id) : [],
      appointments: list,
    };
    // Domingo sem atendimento nem consulta não ocupa espaço na semana.
  }).filter(
    column =>
      weekdayOf(column.date) !== 0 ||
      column.periods.length ||
      column.appointments.length
  );
});

const columns = computed(() =>
  view.value === 'week' ? weekColumns.value : dayColumns.value
);

// Faixa de horas visível: da primeira à última hora com atendimento ou consulta.
const hourRange = computed(() => {
  const minutes = columns.value.flatMap(column => [
    ...column.periods.flatMap(period => [period.start, period.end]),
    ...column.appointments.flatMap(item => [item.start, item.end]),
  ]);
  if (!minutes.length) return [DEFAULT_START, DEFAULT_END];
  const start = Math.floor(Math.min(...minutes) / 60) * 60;
  const end = Math.ceil(Math.max(...minutes) / 60) * 60;
  return [Math.max(0, start), Math.min(24 * 60, Math.max(end, start + 60))];
});

const fetchRange = async () => {
  isLoadingRange.value = true;
  try {
    const params = { from: rangeStart.value, to: rangeEnd.value };
    const [appointmentsResponse, blocksResponse] = await Promise.all([
      agendaAPI.getAppointments(params),
      agendaAPI.getBlocks(params),
    ]);
    appointments.value = appointmentsResponse.data.payload;
    blocks.value = blocksResponse.data.payload;
  } catch {
    useAlert(t('AGENDA.PAGE.LOAD_ERROR'));
  } finally {
    isLoadingRange.value = false;
  }
};

const move = direction => {
  date.value = addDays(date.value, direction * rangeDays.value);
};

const goToday = () => {
  date.value = today.value;
};

const setView = value => {
  view.value = value;
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, value);
  } catch {
    // Sem armazenamento local (janela anônima etc.): só não lembra a visão.
  }
};

const onSelectSlot = ({ column, minute }) => {
  appointmentDialog.value?.open({
    professionalId: column.professionalId,
    date: column.date,
    time: minutesToTime(minute),
  });
};

const onOpenAppointment = appointment => {
  const original = appointments.value.find(item => item.id === appointment.id);
  appointmentDialog.value?.open({ appointment: original || appointment });
};

const newAppointment = () => {
  appointmentDialog.value?.open({
    professionalId: professionalFilter.value ? Number(professionalFilter.value) : '',
    date: date.value < today.value ? today.value : date.value,
  });
};

const goToProfessionals = () => {
  router.push({
    name: 'agenda_professionals_index',
    params: { accountId: route.params.accountId },
  });
};

watch([rangeStart, rangeEnd], fetchRange);
watch([view, date], ([currentView, currentDate]) => {
  router.replace({ query: { ...route.query, view: currentView, date: currentDate } });
});

onMounted(async () => {
  try {
    await fetchAll();
    date.value = queryDate() || todayIn(setting.value.timezone);
  } catch {
    useAlert(t('AGENDA.PAGE.LOAD_ERROR'));
  }
  fetchRange();
  refreshTimer = setInterval(() => {
    now.value = new Date();
    if (document.visibilityState === 'visible') fetchRange();
  }, REFRESH_INTERVAL);
});

onBeforeUnmount(() => clearInterval(refreshTimer));
</script>

<template>
  <AgendaPageLayout
    :title="t('AGENDA.PAGE.TITLE')"
    :description="t('AGENDA.PAGE.DESCRIPTION')"
    wide
  >
    <template #actions>
      <Button
        v-if="professionals.length"
        :label="t('AGENDA.PAGE.NEW')"
        icon="i-lucide-calendar-plus"
        size="sm"
        data-testid="agenda-new-appointment"
        @click="newAppointment"
      />
    </template>

    <template v-if="professionals.length" #toolbar>
      <div class="flex flex-wrap items-center gap-2 pb-2">
        <Button
          :label="t('AGENDA.PAGE.TODAY')"
          variant="faded"
          color="slate"
          size="sm"
          @click="goToday"
        />
        <Button
          icon="i-lucide-chevron-left"
          variant="ghost"
          color="slate"
          size="sm"
          :aria-label="t('AGENDA.PAGE.PREVIOUS')"
          @click="move(-1)"
        />
        <Button
          icon="i-lucide-chevron-right"
          variant="ghost"
          color="slate"
          size="sm"
          :aria-label="t('AGENDA.PAGE.NEXT')"
          @click="move(1)"
        />
        <span
          class="text-heading-2 text-n-slate-12 first-letter:uppercase"
          data-testid="agenda-range-label"
        >
          {{ rangeLabel }}
        </span>
        <Spinner v-if="isLoadingRange" class="size-4 text-n-slate-10" />

        <div class="flex flex-wrap items-center gap-2 ms-auto">
          <div class="flex p-0.5 rounded-lg bg-n-alpha-2">
            <Button
              :label="t('AGENDA.PAGE.VIEW_DAY')"
              :variant="view === 'day' ? 'solid' : 'ghost'"
              :color="view === 'day' ? 'blue' : 'slate'"
              size="sm"
              @click="setView('day')"
            />
            <Button
              :label="t('AGENDA.PAGE.VIEW_WEEK')"
              :variant="view === 'week' ? 'solid' : 'ghost'"
              :color="view === 'week' ? 'blue' : 'slate'"
              size="sm"
              data-testid="agenda-view-week"
              @click="setView('week')"
            />
          </div>
          <Select
            v-model="professionalFilter"
            :options="filterOptions"
            :aria-label="t('AGENDA.PAGE.ALL_PROFESSIONALS')"
          />
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-4 pb-1 text-label-small text-n-slate-11">
        <span class="inline-flex items-center gap-1.5">
          <span class="size-3 rounded-sm bg-n-solid-1 outline outline-1 outline-n-weak" />
          {{ t('AGENDA.PAGE.LEGEND_AVAILABLE') }}
        </span>
        <span class="inline-flex items-center gap-1.5">
          <span class="size-3 rounded-sm bg-n-slate-4" />
          {{ t('AGENDA.PAGE.LEGEND_BLOCKED') }}
        </span>
        <span class="inline-flex items-center gap-1.5">
          <span class="size-3 rounded-sm bg-n-slate-3" />
          {{ t('AGENDA.PAGE.LEGEND_PAST') }}
        </span>
        <span class="inline-flex items-center gap-1.5">
          <span class="i-lucide-sparkles size-3 text-n-brand" />
          {{ t('AGENDA.PAGE.LEGEND_AI') }}
        </span>
        <span class="ms-auto">
          {{ t('AGENDA.PAGE.TIMEZONE', { timezone: setting.timezone }) }}
        </span>
      </div>
    </template>

    <div v-if="!hasLoaded" class="flex justify-center py-16">
      <Spinner />
    </div>

    <div
      v-else-if="!professionals.length"
      class="flex flex-col items-center gap-3 px-6 py-16 text-center rounded-xl outline-1 outline-dashed outline-n-weak"
    >
      <span class="i-lucide-calendar-days size-8 text-n-slate-9" />
      <h2 class="text-heading-2 text-n-slate-12">
        {{ t('AGENDA.PAGE.EMPTY_TITLE') }}
      </h2>
      <p class="text-body-para text-n-slate-11 max-w-md">
        {{
          isAdmin
            ? t('AGENDA.PAGE.EMPTY_DESCRIPTION_ADMIN')
            : t('AGENDA.PAGE.EMPTY_DESCRIPTION_AGENT')
        }}
      </p>
      <Button
        v-if="isAdmin"
        :label="t('AGENDA.PAGE.GO_TO_PROFESSIONALS')"
        icon="i-lucide-user-plus"
        size="sm"
        @click="goToProfessionals"
      />
    </div>

    <AgendaCalendar
      v-else
      :columns="columns"
      :start-minute="hourRange[0]"
      :end-minute="hourRange[1]"
      :today="today"
      :now-minute="nowMinute"
      :show-professional-name="view === 'week' && !professionalFilter"
      :column-min-width="view === 'week' ? '8rem' : '11rem'"
      @select-slot="onSelectSlot"
      @open-appointment="onOpenAppointment"
    />

    <AppointmentDialog ref="appointmentDialog" @saved="fetchRange" />
  </AgendaPageLayout>
</template>
